import React, { useState } from "react";
import { T, FAMILY, FONT } from "../theme/tokens.js";
import { APP_NAME } from "../config.js";
import { Btn, Field, Label, Labelled, Notice, Wordmark } from "../components/ui/index.jsx";
import { useAuth } from "../auth/AuthProvider.jsx";
import { friendlyError } from "../lib/supabase.js";

/* Where somebody without a password of their own ends up.
 *
 * Two ways to arrive. A reset link, which signs the person in for one purpose
 * only. Or a temporary password Heidi issued and read out, which opens this
 * screen and nothing else. Both mean the same thing: no password of their own
 * exists yet, and until one does they reach nothing.
 *
 * Without this screen the app simply carried them into the workspace, they
 * never set anything, and the next time they came back they were locked out
 * again with no idea why.
 */

const MIN = 8;

export default function NewPassword() {
  const { savePassword, finishRecovery, refreshProfile, signOut, user, mustSetPassword } = useAuth();
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const tooShort = password.length > 0 && password.length < MIN;
  const mismatch = again.length > 0 && again !== password;
  const ready = password.length >= MIN && again === password;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setError("");
    setBusy(true);
    const { error: failed } = await savePassword(password);
    setBusy(false);
    if (failed) setError(friendlyError(failed));
    else {
      finishRecovery();
      /* The flag lives on the profile, so the app only stops asking once the
         profile has been read again. */
      await refreshProfile();
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: T.navy, display: "flex",
      alignItems: "center", justifyContent: "center", fontFamily: FAMILY, padding: 16,
    }}>
      <style>{FONT}</style>
      <div style={{
        background: T.panel, border: `1px solid ${T.line}`,
        borderTop: `4px solid ${T.gold}`, borderRadius: 6, padding: 36,
        width: "100%", maxWidth: 400,
      }}>
        <Wordmark onLight />
        <div style={{ marginTop: 12 }}><Label>{APP_NAME}</Label></div>
        <div style={{ fontSize: 13, color: T.soft, margin: "6px 0 22px" }}>
          {mustSetPassword ? "Choose your own password" : "Choose a new password"}
          {user?.email ? ` for ${user.email}` : ""}
        </div>

        <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
          <Labelled label="New password" hint={`At least ${MIN} characters`}>
            <Field f={{ type: "password" }} value={password} onChange={setPassword}
              autoFocus placeholder={`At least ${MIN} characters`} />
          </Labelled>

          <Labelled label="Type it again">
            <Field f={{ type: "password" }} value={again} onChange={setAgain}
              placeholder="The same again" />
          </Labelled>

          {tooShort && <Notice>Passwords need at least {MIN} characters.</Notice>}
          {mismatch && <Notice>Those two do not match yet.</Notice>}
          {error && <Notice kind="error">{error}</Notice>}

          <Btn type="submit" full disabled={busy || !ready}>
            {busy ? "Saving..." : "Save password and continue"}
          </Btn>

          <button type="button" onClick={signOut}
            style={{ background: "none", border: "none", color: T.soft,
                     fontSize: 12.5, cursor: "pointer", padding: 0 }}>
            {mustSetPassword ? "Sign out instead" : "Cancel and go back to sign in"}
          </button>
        </form>

        <div style={{ fontSize: 11.5, color: T.soft, marginTop: 18, lineHeight: 1.6 }}>
          {mustSetPassword
            ? "The password you were given works once, to get you here. This is the "
              + "one you keep. Save it somewhere before you close this page, because "
              + "nobody, Heidi included, can read it back to you."
            : "Save it somewhere before you close this page. Nobody, Heidi included, "
              + "can read your password back to you."}
        </div>
      </div>
    </div>
  );
}
